import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Grid2 as Grid, Button, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Alert, LinearProgress,
} from '@mui/material';
import { Assessment as ReportIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { posAdminService, PosRelatorio } from '../../services/pos-admin.service';
import { dayCv, todayCv } from '../../utils/date';

const cve = (n: number) => `${n.toLocaleString('pt-PT')}$`;

/** Resumo do POS por período: por terminal, numerário e fechos. */
export default function PosRelatorios() {
  const hoje = todayCv();
  const ha30 = dayCv(new Date(Date.now() - 30 * 864e5));

  const [dados, setDados] = useState<PosRelatorio | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [from, setFrom] = useState(ha30);
  const [to, setTo] = useState(hoje);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setDados(await posAdminService.relatorios({ from, to }));
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Não foi possível carregar o relatório.');
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const maxTerminal = Math.max(1, ...(dados?.porTerminal ?? []).map((t) => t.total));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
            <ReportIcon color="primary" /> Relatórios POS
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Resumo do período por terminal, com o numerário recebido à parte.
          </Typography>
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Actualizar</Button>
      </Box>

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      <Card>
        <CardContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6, sm: 3 }}>
              <TextField fullWidth size="small" type="date" label="De" InputLabelProps={{ shrink: true }}
                value={from} onChange={(e) => setFrom(e.target.value)} />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <TextField fullWidth size="small" type="date" label="Até" InputLabelProps={{ shrink: true }}
                value={to} onChange={(e) => setTo(e.target.value)} />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : dados && (
        <>
          {/* Numerário — separado de propósito: é dinheiro físico que tem de
              estar na gaveta, e não se soma aos pagamentos electrónicos. */}
          <Card>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Carregamentos em numerário
              </Typography>
              <Grid container spacing={2}>
                {[
                  { l: 'Operações', v: String(dados.numerario.operacoes) },
                  { l: 'Recebido em mão', v: cve(dados.numerario.recebido), d: true },
                  { l: 'Taxa cobrada', v: cve(dados.numerario.taxa) },
                  { l: 'Creditado a clientes', v: cve(dados.numerario.creditado) },
                  { l: 'Por liquidar', v: cve(dados.numerario.aLiquidar), alerta: dados.numerario.aLiquidar > 0 },
                ].map((c) => (
                  <Grid key={c.l} size={{ xs: 6, sm: 4, md: 2.4 }}>
                    <Typography variant="caption" color="text.secondary">{c.l}</Typography>
                    <Typography variant="h6" fontWeight={700}
                      sx={{ fontVariantNumeric: 'tabular-nums' }}
                      color={c.alerta ? 'warning.main' : c.d ? 'primary.main' : 'text.primary'}>
                      {c.v}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Pedidos de pagamento por terminal
              </Typography>
              {dados.porTerminal.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                  Sem pedidos de pagamento no período.
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: 600 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Terminal</TableCell>
                        <TableCell align="right">Pedidos</TableCell>
                        <TableCell align="right">Pagos</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell sx={{ width: '30%' }} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dados.porTerminal.map((t) => (
                        <TableRow key={t.terminalId ?? t.terminalNome} hover>
                          <TableCell sx={{ fontWeight: 500 }}>{t.terminalNome}</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{t.pedidos}</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{t.pagos}</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                            {cve(t.total)}
                          </TableCell>
                          <TableCell>
                            <LinearProgress variant="determinate"
                              value={(t.total / maxTerminal) * 100}
                              sx={{ height: 6, borderRadius: 3 }} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Fechos de caixa</Typography>
              <Typography variant="body2" color="text.secondary">
                {dados.fechos.count === 0
                  ? 'Nenhum fecho de caixa no período.'
                  : `${dados.fechos.count} fecho(s), ${cve(dados.fechos.total)} movimentados.`}
              </Typography>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
}
